"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type DistributionError,
  type ExpectedValueReport,
  expectedValue,
  isDistributionError,
  parseOutcomes,
  type WeightKind,
} from "@/lib/tool-kit/stats-descriptive/expected-value";
import {
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";

/** English strings. A translated content file overrides these through `ui`. */
export const EXPECTED_VALUE_CALCULATOR_STRINGS = {
  kindLabel: "The second number on each line is",
  kindProbability: "A probability",
  kindFrequency: "A frequency (count)",
  dataLabel: "Outcomes — one per line: the value, then its probability",
  dataLabelFrequency:
    "Outcomes — one per line: the value, then how often it occurred",
  dataPlaceholder: "3, 1/6\n-1, 5/6",
  dataHint:
    "Probabilities can be decimals (0.25), percentages (25%) or fractions (1/4). Words before the numbers are ignored, so you can label each line.",
  outcomesRead: "one: {count} outcome read.|other: {count} outcomes read.",
  skippedLines:
    "one: Line {lines} does not hold two numbers, so it was skipped.|other: Lines {lines} do not hold two numbers, so they were skipped.",
  examplesLabel: "Try an example",
  exampleGame: "Dice game",
  exampleInsurance: "Insurance policy",
  exampleLottery: "Lottery ticket",
  gameData: "Win: roll a six, 3, 1/6\nLose: anything else, -1, 5/6",
  insuranceData: "No claim, 200, 0.998\nClaim paid out, -49800, 0.002",
  lotteryData: "Win the prize, 499, 0.001\nLose the stake, -1, 0.999",
  clear: "Clear",
  enterPrompt: "Enter at least two outcomes, one per line.",
  errorNegative: "Probabilities and frequencies cannot be negative.",
  errorAboveOne:
    "A probability cannot be more than 1 (100%). If these are counts, switch to frequencies.",
  errorZeroTotal: "The frequencies add up to zero.",
  errorSum:
    "The probabilities add up to {sum}, not 1. Check them — or, if they are counts, switch to frequencies.",
  roundedNote:
    "The probabilities add up to {sum} — close enough to 1 to be rounding, so they are used as entered.",
  results: "Results",
  expectedValue: "Expected value E(X)",
  variance: "Variance Var(X)",
  sd: "Standard deviation SD(X)",
  sumP: "Sum of the probabilities",
  outcomes: "Outcomes",
  tableTitle: "Working table",
  colX: "x",
  colFrequency: "Frequency",
  colP: "P(x)",
  colXp: "x × P(x)",
  colDeviation: "(x − μ)² × P(x)",
  total: "Sum",
  workingTitle: "How it was calculated",
  frequencyLine:
    "Each frequency is divided by the total, {total}, to give P(x).",
  meanLine: "E(X) = Σ x × P(x) = {terms} = {mean}",
  meanShortLine: "E(X) = Σ x × P(x) = {mean}",
  varianceLine: "Var(X) = Σ (x − μ)² × P(x) = {variance}",
  sdLine: "SD(X) = √Var(X) = √{variance} = {sd}",
  interpretation:
    "E(X) = {mean} is the long-run average: repeat the process many times and the average outcome settles near it, although no single outcome need equal it.",
} as const;

type Strings = typeof EXPECTED_VALUE_CALCULATOR_STRINGS;

/** Spell out Σ x × P(x) term by term while it stays readable. */
const TERM_LIMIT = 6;

function errorMessage(
  error: DistributionError,
  strings: Strings,
  fmt: NumberFormatter
): string {
  switch (error.error) {
    case "negative-weight":
      return strings.errorNegative;
    case "probability-above-one":
      return strings.errorAboveOne;
    case "zero-total":
      return strings.errorZeroTotal;
    default:
      return t(strings.errorSum, { sum: formatStat(fmt, error.sum) });
  }
}

function Working({
  report,
  fmt,
  strings,
}: {
  report: ExpectedValueReport;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const num = (value: number) => formatStat(fmt, value);
  const signed = (value: number) =>
    value < 0 ? `(${num(value)})` : num(value);
  const terms = report.rows
    .map((row) => `${signed(row.x)} × ${num(row.p)}`)
    .join(" + ");
  return (
    <WorkingPanel title={strings.workingTitle}>
      {report.totalFrequency === null ? null : (
        <Formula>
          {t(strings.frequencyLine, { total: num(report.totalFrequency) })}
        </Formula>
      )}
      <Formula>
        {report.rows.length <= TERM_LIMIT
          ? t(strings.meanLine, { terms, mean: num(report.mean) })
          : t(strings.meanShortLine, { mean: num(report.mean) })}
      </Formula>
      <Formula>
        {t(strings.varianceLine, { variance: num(report.variance) })}
      </Formula>
      <Formula>
        {t(strings.sdLine, {
          variance: num(report.variance),
          sd: num(report.sd),
        })}
      </Formula>
      <Explanation>
        {t(strings.interpretation, { mean: num(report.mean) })}
      </Explanation>
    </WorkingPanel>
  );
}

function Results({
  report,
  fmt,
  strings,
}: {
  report: ExpectedValueReport;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const num = (value: number) => formatStat(fmt, value);
  const frequency = report.totalFrequency !== null;
  const head = [
    strings.colX,
    ...(frequency ? [strings.colFrequency] : []),
    strings.colP,
    strings.colXp,
    strings.colDeviation,
  ];
  return (
    <section className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {report.roundedSum ? (
        <Notice>{t(strings.roundedNote, { sum: num(report.sumP) })}</Notice>
      ) : null}
      <div aria-live="polite">
        <StatGrid>
          <StatCard
            emphasis
            label={strings.expectedValue}
            value={num(report.mean)}
          />
          <StatCard label={strings.variance} value={num(report.variance)} />
          <StatCard label={strings.sd} value={num(report.sd)} />
          <StatCard label={strings.sumP} value={num(report.sumP)} />
          <StatCard label={strings.outcomes} value={fmt(report.rows.length)} />
        </StatGrid>
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.tableTitle}</h3>
        <ResultTable
          footer={[
            strings.total,
            ...(frequency ? [num(report.totalFrequency as number)] : []),
            num(report.sumP),
            num(report.mean),
            num(report.variance),
          ]}
          head={head}
          rows={report.rows.map((row) => [
            num(row.x),
            ...(frequency
              ? [num(row.p * (report.totalFrequency as number))]
              : []),
            num(row.p),
            num(row.xp),
            num(row.squaredDeviationP),
          ])}
        />
      </div>
      <Working fmt={fmt} report={report} strings={strings} />
    </section>
  );
}

export default function ExpectedValueCalculatorWidget() {
  const strings = useToolUi(EXPECTED_VALUE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [kind, setKind] = useState<WeightKind>("probability");
  const [raw, setRaw] = useState("");

  const parsed = parseOutcomes(raw);
  const outcome =
    parsed.rows.length >= 2 ? expectedValue(parsed.rows, kind) : null;
  const problem =
    outcome && isDistributionError(outcome)
      ? errorMessage(outcome, strings, fmt)
      : null;
  const report = outcome && !isDistributionError(outcome) ? outcome : null;
  const skipped = parsed.invalidLines.length;

  function load(example: string) {
    setKind("probability");
    setRaw(example);
  }

  return (
    <>
      <section className="space-y-4">
        <PillGroup
          label={strings.kindLabel}
          name="ev-kind"
          onChange={setKind}
          options={[
            { value: "probability", label: strings.kindProbability },
            { value: "frequency", label: strings.kindFrequency },
          ]}
          value={kind}
        />
        <TextAreaField
          hint={
            <>
              {tPlural(strings.outcomesRead, locale, parsed.rows.length, {
                count: fmt(parsed.rows.length),
              })}{" "}
              {strings.dataHint}
            </>
          }
          id="ev-data"
          label={
            kind === "probability"
              ? strings.dataLabel
              : strings.dataLabelFrequency
          }
          onChange={setRaw}
          placeholder={strings.dataPlaceholder}
          value={raw}
        />
        {skipped > 0 ? (
          <Notice>
            {tPlural(strings.skippedLines, locale, skipped, {
              lines: parsed.invalidLines.map((line) => fmt(line)).join(", "),
            })}
          </Notice>
        ) : null}
        <div className="space-y-2">
          <p className={LABEL}>{strings.examplesLabel}</p>
          <div className="flex flex-wrap gap-3">
            <button
              className={BUTTON_SECONDARY}
              onClick={() => load(strings.gameData)}
              type="button"
            >
              {strings.exampleGame}
            </button>
            <button
              className={BUTTON_SECONDARY}
              onClick={() => load(strings.insuranceData)}
              type="button"
            >
              {strings.exampleInsurance}
            </button>
            <button
              className={BUTTON_SECONDARY}
              onClick={() => load(strings.lotteryData)}
              type="button"
            >
              {strings.exampleLottery}
            </button>
            <button
              className={BUTTON_SECONDARY}
              onClick={() => setRaw("")}
              type="button"
            >
              {strings.clear}
            </button>
          </div>
        </div>
      </section>

      {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
      {report ? <Results fmt={fmt} report={report} strings={strings} /> : null}
      {outcome ? null : <Notice>{strings.enterPrompt}</Notice>}
    </>
  );
}
